import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { WebRTCProvider, useWebRTCContext } from './WebRTCContext';
import useWebRTC from './useWebRTC';
import AudioVisualizer from './AudioVisualizer';
import AudioControls from './AudioControls';
import ConversationDisplay from './ConversationDisplay';
import * as S from './styled';

// Play icon for the overlay button
const PlayIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="white">
    <path d="M8 5v14l11-7z" />
  </svg>
);

// Internal component that uses context
const WebRTCDemoInternal = ({ 
  userName, 
  autoStart,
  onConversationStart, 
  onConversationEnd, 
  onError, 
  onTranscript 
}) => {
  const { state } = useWebRTCContext();
  const { connectionState, error } = state;
  
  // Initialize WebRTC hook with real connection functionality
  const { 
    connect, 
    disconnect, 
    toggleMute,
    isMuted,
    isCallSetupComplete
  } = useWebRTC({
    userName,
  });
  
  // Auto-start connection if autoStart prop is true
  useEffect(() => {
    if (autoStart && connectionState === 'idle') {
      connect();
    }
  }, [autoStart, connectionState, connect]);
  
  // Handle state changes and call callbacks
  useEffect(() => {
    if (connectionState === 'connected' && onConversationStart) {
      onConversationStart();
    }
    
    if (connectionState === 'disconnected' && onConversationEnd) {
      onConversationEnd();
    }
    
    if (connectionState === 'error' && onError) {
      onError(error);
    }
  }, [connectionState, error, onConversationStart, onConversationEnd, onError]);
  
  // Handle transcript updates
  useEffect(() => {
    if (onTranscript && state.transcript.length > 0) {
      const latestItem = state.transcript[state.transcript.length - 1];
      onTranscript(latestItem);
    }
  }, [state.transcript, onTranscript]);
  
  const handleStartCall = () => {
    // Use our new WebRTC connect function
    connect();
  };
  
  const renderInitialState = () => (
    <div style={{ position: 'relative', height: '100%', overflow: 'hidden' }}>
      <div style={{ 
        position: 'absolute', 
        top: '50%', 
        left: '50%', 
        transform: 'translate(-50%, -200%)',
        zIndex: 20,
        width: '80%',
        textAlign: 'center'
      }}>
        <S.IntroText>
          Talk to Dasha now!
        </S.IntroText>
      </div>
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(18, 23, 36, 0.5)',
        cursor: 'pointer',
        zIndex: 10
      }} onClick={handleStartCall}>
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          backgroundColor: '#8850FF',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          boxShadow: '0 8px 24px rgba(136, 80, 255, 0.4)',
          transition: 'all 0.3s ease',
          marginTop: '40px'
        }}>
          <PlayIcon />
        </div>
      </div>
    </div>
  );
  
  const renderActiveState = () => (
    <S.MainLayout>
      <S.TranscriptSection>
        <ConversationDisplay />
      </S.TranscriptSection>
      {error && (
        <S.ErrorMessage>
          Error: {error.message || 'An error occurred during the conversation.'}
        </S.ErrorMessage>
      )}
    </S.MainLayout>
  );
  
  return (
    <S.Container>
      {/* The S.Header block previously here has been removed. */}
      
      <S.Content noHeader={true}>
        {connectionState === 'idle' 
          ? renderInitialState() 
          : renderActiveState()
        }
      </S.Content>
      
      {/* FooterBar is now shown if connected AND setup, OR if disconnected, OR if connecting */}
      {((connectionState === 'connected' && isCallSetupComplete) || connectionState === 'disconnected' || connectionState === 'connecting') && (
        <S.FooterBar>
          {/* Only show AudioVisualizer if actually connected and call setup is complete */}
          {connectionState === 'connected' && isCallSetupComplete && <AudioVisualizer />}
          <AudioControls 
            onToggleMute={toggleMute}
            onEndCall={disconnect}
            onRestartCall={connect}
            isMuted={isMuted}
          />
        </S.FooterBar>
      )}
    </S.Container>
  );
};

// Wrapper component that provides context
const WebRTCDemo = (props) => {
  return (
    <WebRTCProvider>
      <WebRTCDemoInternal {...props} />
    </WebRTCProvider>
  );
};

WebRTCDemo.propTypes = {
  agentConfig: PropTypes.object,
  userName: PropTypes.string,
  autoStart: PropTypes.bool,
  showTranscript: PropTypes.bool,
  theme: PropTypes.object,
  onConversationStart: PropTypes.func,
  onConversationEnd: PropTypes.func,
  onError: PropTypes.func,
  onTranscript: PropTypes.func
};

WebRTCDemo.defaultProps = {
  agentConfig: {},
  userName: 'User',
  autoStart: false,
  showTranscript: true,
  theme: {},
  onConversationStart: null,
  onConversationEnd: null,
  onError: null,
  onTranscript: null
};

export default WebRTCDemo; 